import React, { Fragment, useState, useMemo } from 'react';
import { Input, Button, Row, Col } from 'reactstrap';
import { CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import { Copy, RotateCcw, Shuffle } from 'react-feather';

/**
 * JsonDiffModal — Paste two JSON objects side by side and see the differences.
 * Highlights added, removed, and changed keys with color coding.
 */

const DIFF_COLORS = {
    added: { bg: 'rgba(40, 167, 69, 0.08)', border: 'rgba(40, 167, 69, 0.3)', text: '#28a745' },
    removed: { bg: 'rgba(220, 53, 69, 0.08)', border: 'rgba(220, 53, 69, 0.3)', text: '#dc3545' },
    changed: { bg: 'rgba(255, 193, 7, 0.08)', border: 'rgba(255, 193, 7, 0.3)', text: '#d39e00' },
    unchanged: { bg: 'transparent', border: 'transparent', text: '#666' },
};

/**
 * Deep diff between two objects. Returns an array of diff entries.
 */
function deepDiff(obj1, obj2, path = '') {
    const diffs = [];

    const allKeys = new Set([
        ...Object.keys(obj1 || {}),
        ...Object.keys(obj2 || {}),
    ]);

    for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const inLeft = obj1 !== null && obj1 !== undefined && key in obj1;
        const inRight = obj2 !== null && obj2 !== undefined && key in obj2;

        if (!inLeft && inRight) {
            diffs.push({ path: currentPath, type: 'added', left: undefined, right: obj2[key] });
        } else if (inLeft && !inRight) {
            diffs.push({ path: currentPath, type: 'removed', left: obj1[key], right: undefined });
        } else {
            const leftVal = obj1[key];
            const rightVal = obj2[key];

            if (typeof leftVal === 'object' && leftVal !== null && typeof rightVal === 'object' && rightVal !== null) {
                // Both are objects (or arrays) — recurse
                if (Array.isArray(leftVal) && Array.isArray(rightVal)) {
                    if (JSON.stringify(leftVal) !== JSON.stringify(rightVal)) {
                        diffs.push({ path: currentPath, type: 'changed', left: leftVal, right: rightVal });
                    } else {
                        diffs.push({ path: currentPath, type: 'unchanged', left: leftVal, right: rightVal });
                    }
                } else if (Array.isArray(leftVal) !== Array.isArray(rightVal)) {
                    diffs.push({ path: currentPath, type: 'changed', left: leftVal, right: rightVal });
                } else {
                    const nested = deepDiff(leftVal, rightVal, currentPath);
                    diffs.push(...nested);
                }
            } else if (leftVal !== rightVal) {
                diffs.push({ path: currentPath, type: 'changed', left: leftVal, right: rightVal });
            } else {
                diffs.push({ path: currentPath, type: 'unchanged', left: leftVal, right: rightVal });
            }
        }
    }

    return diffs;
}

const formatValue = (val) => {
    if (val === undefined) return '—';
    if (val === null) return 'null';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    if (typeof val === 'string') return `"${val}"`;
    return String(val);
};

const JsonDiffModal = ({ trigger }) => {

    const [modal, setModal] = useState(false);
    const [leftInput, setLeftInput] = useState('');
    const [rightInput, setRightInput] = useState('');
    const [showUnchanged, setShowUnchanged] = useState(false);

    const openModal = () => setModal(true);

    const handleClear = () => {
        setLeftInput('');
        setRightInput('');
    };

    const handleSwap = () => {
        setLeftInput(rightInput);
        setRightInput(leftInput);
    };

    const handleFormat = (side) => {
        try {
            if (side === 'left') {
                const parsed = JSON.parse(leftInput);
                setLeftInput(JSON.stringify(parsed, null, 2));
            } else {
                const parsed = JSON.parse(rightInput);
                setRightInput(JSON.stringify(parsed, null, 2));
            }
        } catch (e) {
            // silently ignore — user will see the parse error in results
        }
    };

    const { diffs, leftError, rightError } = useMemo(() => {
        let leftObj = null, rightObj = null;
        let leftErr = null, rightErr = null;

        if (leftInput.trim()) {
            try { leftObj = JSON.parse(leftInput); }
            catch (e) { leftErr = e.message; }
        }
        if (rightInput.trim()) {
            try { rightObj = JSON.parse(rightInput); }
            catch (e) { rightErr = e.message; }
        }

        if (leftErr || rightErr || !leftObj || !rightObj) {
            return { diffs: null, leftError: leftErr, rightError: rightErr };
        }

        return { diffs: deepDiff(leftObj, rightObj), leftError: null, rightError: null };
    }, [leftInput, rightInput]);

    const diffSummary = useMemo(() => {
        if (!diffs) return null;
        const added = diffs.filter(d => d.type === 'added').length;
        const removed = diffs.filter(d => d.type === 'removed').length;
        const changed = diffs.filter(d => d.type === 'changed').length;
        const unchanged = diffs.filter(d => d.type === 'unchanged').length;
        return { added, removed, changed, unchanged, total: diffs.length };
    }, [diffs]);

    const filteredDiffs = useMemo(() => {
        if (!diffs) return [];
        if (showUnchanged) return diffs;
        return diffs.filter(d => d.type !== 'unchanged');
    }, [diffs, showUnchanged]);

    const renderDiffResults = () => {
        if (!leftInput.trim() && !rightInput.trim()) {
            return <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>Paste JSON in both panels to compare</p>;
        }
        if (leftError || rightError) {
            return (
                <div style={{ padding: 16 }}>
                    {leftError && <p style={{ color: '#dc3545', fontSize: 12 }}><strong>Left JSON Error:</strong> {leftError}</p>}
                    {rightError && <p style={{ color: '#dc3545', fontSize: 12 }}><strong>Right JSON Error:</strong> {rightError}</p>}
                </div>
            );
        }
        if (!leftInput.trim() || !rightInput.trim()) {
            return <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>Paste JSON in both panels to compare</p>;
        }

        if (filteredDiffs.length === 0 && !showUnchanged) {
            return <p style={{ color: '#28a745', textAlign: 'center', padding: 20 }}>✓ Objects are identical</p>;
        }

        return (
            <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                {filteredDiffs.map((diff, i) => {
                    const colors = DIFF_COLORS[diff.type];
                    return (
                        <div key={i} style={{
                            padding: '8px 12px',
                            marginBottom: 4,
                            borderRadius: 4,
                            background: colors.bg,
                            borderLeft: `3px solid ${colors.border}`,
                            fontSize: 12,
                        }}>
                            <div className='d-flex justify-content-between align-items-start'>
                                <span style={{ fontWeight: 600, color: colors.text, fontFamily: 'monospace' }}>
                                    {diff.type === 'added' && '+ '}
                                    {diff.type === 'removed' && '− '}
                                    {diff.type === 'changed' && '~ '}
                                    {diff.path}
                                </span>
                                <span style={{ fontSize: 10, color: colors.text, textTransform: 'uppercase', fontWeight: 600 }}>
                                    {diff.type}
                                </span>
                            </div>
                            {diff.type !== 'unchanged' && (
                                <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'monospace' }}>
                                    {diff.type === 'removed' && <div style={{ color: '#dc3545' }}>{formatValue(diff.left)}</div>}
                                    {diff.type === 'added' && <div style={{ color: '#28a745' }}>{formatValue(diff.right)}</div>}
                                    {diff.type === 'changed' && (
                                        <>
                                            <div style={{ color: '#dc3545', textDecoration: 'line-through', opacity: 0.7 }}>{formatValue(diff.left)}</div>
                                            <div style={{ color: '#28a745' }}>{formatValue(diff.right)}</div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Paste two JSON objects to compare. Differences are highlighted by type: added, removed, or changed.
            </p>

            {/* Input panels */}
            <Row style={{ marginBottom: 12 }}>
                <Col md="6">
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>Left (Original)</span>
                        <Button size='sm' color='light' onClick={() => handleFormat('left')} title='Format JSON' style={{ fontSize: 10, padding: '2px 8px' }}>
                            Format
                        </Button>
                    </div>
                    <Input
                        type='textarea'
                        value={leftInput}
                        onChange={(e) => setLeftInput(e.target.value)}
                        placeholder='{"key": "value", ...}'
                        style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 160, resize: 'vertical' }}
                    />
                </Col>
                <Col md="6">
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>Right (New)</span>
                        <Button size='sm' color='light' onClick={() => handleFormat('right')} title='Format JSON' style={{ fontSize: 10, padding: '2px 8px' }}>
                            Format
                        </Button>
                    </div>
                    <Input
                        type='textarea'
                        value={rightInput}
                        onChange={(e) => setRightInput(e.target.value)}
                        placeholder='{"key": "value", ...}'
                        style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 160, resize: 'vertical' }}
                    />
                </Col>
            </Row>

            {/* Controls */}
            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 12 }}>
                <div className='d-flex gap-2'>
                    <Button size='sm' color='light' onClick={handleSwap} title='Swap left and right'>
                        <Shuffle size={12} className='me-1' /> Swap
                    </Button>
                    <Button size='sm' color='light' onClick={handleClear} title='Clear both'>
                        <RotateCcw size={12} className='me-1' /> Clear
                    </Button>
                </div>
                <div className='d-flex align-items-center gap-2'>
                    {diffSummary && (
                        <div style={{ fontSize: 11 }}>
                            <span style={{ color: DIFF_COLORS.added.text, fontWeight: 600 }}>+{diffSummary.added}</span>
                            <span style={{ margin: '0 4px', color: '#ccc' }}>|</span>
                            <span style={{ color: DIFF_COLORS.removed.text, fontWeight: 600 }}>−{diffSummary.removed}</span>
                            <span style={{ margin: '0 4px', color: '#ccc' }}>|</span>
                            <span style={{ color: DIFF_COLORS.changed.text, fontWeight: 600 }}>~{diffSummary.changed}</span>
                            <span style={{ margin: '0 4px', color: '#ccc' }}>|</span>
                            <span style={{ color: '#999' }}>{diffSummary.unchanged} same</span>
                        </div>
                    )}
                    <Button size='sm' color={showUnchanged ? 'dark' : 'light'}
                        style={{ fontSize: 10, padding: '3px 8px' }}
                        onClick={() => setShowUnchanged(!showUnchanged)}>
                        {showUnchanged ? 'Hide' : 'Show'} Unchanged
                    </Button>
                    {diffs && (
                        <Copy size={13} style={{ cursor: 'pointer', opacity: 0.4 }} title='Copy diff summary'
                            onClick={() => handleCopy(JSON.stringify(filteredDiffs, null, 2))} />
                    )}
                </div>
            </div>

            {/* Results */}
            <div style={{
                border: '1px solid rgba(128,128,128,0.2)',
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(128,128,128,0.02)',
                padding: 8,
            }}>
                {renderDiffResults()}
            </div>
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='JSON Diff Checker' toggler={() => setModal(false)} size='xl' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
            </Fragment>
        );
    }

    return (
        <CommonModal isOpen={modal} title='JSON Diff Checker' toggler={() => setModal(false)} size='xl' onSubmit={false} onCancelLabel='Close'>
            {modalBody}
        </CommonModal>
    );
};

export default JsonDiffModal;
